import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { DonutChart } from '@/pages/Organization/Analytics/components/DonutChart'
import { GaugeChart } from '@/pages/Organization/Analytics/components/GaugeChart'
import { adminApi } from '@/utils/api'
import type { AdminRiskAnalysis, AdminCompletionRate, AdminMetricsTrends } from '@/types/admin'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'

type FlaggedCouple = { name?: string; riskLevel?: string; coupleId?: string }
// type TrendsResponse = AdminMetricsTrends | { data?: AdminMetricsTrends }

export default function AdminAnalyticsPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [risk, setRisk] = useState<AdminRiskAnalysis | null>(null)
  const [completion, setCompletion] = useState<AdminCompletionRate[]>([])
  const [trends, setTrends] = useState<AdminMetricsTrends>([])
  const [flagged, setFlagged] = useState<FlaggedCouple[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [trendPeriod, setTrendPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('7d')

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [riskRes, compRes, trendsRes, flaggedRes] = await Promise.all([
          adminApi.riskAnalysis(),
          adminApi.completionRates(),
          adminApi.metricsTrends({ period: trendPeriod }),
          adminApi.couplesFlagged(),
        ])
        setRisk((riskRes as { data?: AdminRiskAnalysis }).data || (riskRes as unknown as AdminRiskAnalysis))
        setCompletion(
          (compRes as { data?: AdminCompletionRate[] }).data || (compRes as unknown as AdminCompletionRate[])
        )
        const trendsPayload = trendsRes as { data?: { data?: AdminMetricsTrends } }
        const trendsData =
          trendsPayload.data?.data || (trendsRes as { data?: AdminMetricsTrends }).data || (trendsRes as unknown as AdminMetricsTrends)
        setTrends(Array.isArray(trendsData) ? trendsData : (Array.isArray((trendsData as any)?.data) ? (trendsData as any).data : []))

        const flaggedPayload = flaggedRes as { data?: { couples?: FlaggedCouple[] } }
        setFlagged(flaggedPayload.data?.couples || [])
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load analytics')
      }
    }
    void load()
  }, [trendPeriod])

  const riskChart = useMemo(() => {
    const colors = ['#22C55E', '#F59E0B', '#EF4444']
    if (risk?.labels && risk.values) {
      const total = risk.values.reduce((sum, val) => sum + val, 0)
      return risk.labels.map((label, idx) => ({
        label,
        value: risk.values[idx] || 0,
        color: colors[idx] ?? '#94A3B8',
        percentage: total > 0 ? `${Math.round(((risk.values[idx] || 0) / total) * 100)}%` : '0%'
      }))
    }
    const overall = (risk as { overallDistribution?: { low?: number; moderate?: number; high?: number } })
      ?.overallDistribution
    if (overall) {
      const total = (overall.low ?? 0) + (overall.moderate ?? 0) + (overall.high ?? 0)
      return ['Low', 'Moderate', 'High'].map((label, idx) => {
        const value = idx === 0 ? (overall.low ?? 0) : idx === 1 ? (overall.moderate ?? 0) : (overall.high ?? 0)
        return {
          label,
          value,
          color: colors[idx] ?? '#94A3B8',
          percentage: total > 0 ? `${Math.round((value / total) * 100)}%` : '0%'
        }
      })
    }
    return ['Low', 'Moderate', 'High'].map((label, idx) => ({
      label,
      value: 0,
      color: colors[idx] ?? '#94A3B8',
      percentage: '0%'
    }))
  }, [risk])

  const avgCompletion = completion.length
    ? Math.round(completion.reduce((sum, item) => sum + (item.completionRate ?? 0), 0) / completion.length)
    : 0

  const completionDetails = completion.map((item) => ({
    org: item.organizationName || item.organizationId || 'Org',
    rate: item.completionRate ?? 0,
    total: item.totalAssessments ?? 0,
  }))

  const trendsData = useMemo(
    () =>
      trends.length
        ? trends.map((t, idx) => ({
            label: t.date
              ? new Date(t.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              : `Day ${idx + 1}`,
            value: (t as { users?: number; value?: number }).users ?? t.value ?? 0,
          }))
        : [],
    [trends]
  )

  const compactTrends = useMemo(() => {
    if (trendsData.length <= 30) return trendsData
    const step = Math.max(1, Math.ceil(trendsData.length / 30))
    return trendsData.filter((_, idx) => idx === trendsData.length - 1 || idx % step === 0)
  }, [trendsData])

  const handleSignOut = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Analytics'
          infoText='Risk analysis, completion rates, flagged couples, and trends.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
        {error && <p className='mt-2 text-sm text-red-600 text-start'>{error}</p>}
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-3 gap-4'>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Risk Analysis</h3>
          <DonutChart data={riskChart} />
          <div className='mt-3 text-xs text-gray-700 space-y-1'>
            <p>Total Assessments: {riskChart.reduce((sum, item) => sum + item.value, 0)}</p>
            <p className='flex justify-between'>
              <span>Low</span><span>{riskChart[0]?.value ?? 0}</span>
            </p>
            <p className='flex justify-between'>
              <span>Moderate</span><span>{riskChart[1]?.value ?? 0}</span>
            </p>
            <p className='flex justify-between'>
              <span>High</span><span>{riskChart[2]?.value ?? 0}</span>
            </p>
          </div>
        </div>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Completion Rates</h3>
          <GaugeChart value={avgCompletion} label='Avg Completion' />
          <div className='mt-3 space-y-1 text-xs text-gray-700'>
            {completionDetails.slice(0, 3).map((c, idx) => (
              <div key={`${c.org}-${idx}`} className='flex justify-between'>
                <span className='font-semibold text-gray-900'>{c.org}</span>
                <span className='text-gray-700'>{c.rate}% ({c.total} assessments)</span>
              </div>
            ))}
            {completionDetails.length === 0 && <p className='text-gray-600'>No completion data.</p>}
          </div>
          <p className='text-xs text-gray-600 mt-2'>Per-organization completion comparisons.</p>
        </div>
        <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
          <h3 className='text-lg font-semibold text-gray-900 mb-4'>Flagged Couples</h3>
          <ul className='space-y-2 text-sm text-gray-800'>
            {(flagged && flagged.length ? flagged : []).map((item, idx) => (
              <li key={item.name ?? idx} className='flex items-center justify-between'>
                <span>{item.name ?? `Couple #${idx + 1}`}</span>
                <span className='text-red-600 font-semibold'>{item.riskLevel ?? 'High Risk'}</span>
              </li>
            ))}
            {(!flagged || flagged.length === 0) && <li className='text-gray-600'>No flagged couples.</li>}
          </ul>
        </div>
      </div>

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900'>Metrics Trends</h3>
          <div className='flex items-center gap-2 text-sm'>
            <span className='text-gray-600'>Range</span>
            <select
              value={trendPeriod}
              onChange={(e) => setTrendPeriod(e.target.value as typeof trendPeriod)}
              className='border border-gray-200 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500'
            >
              <option value='7d'>Last 7 days</option>
              <option value='30d'>Last 30 days</option>
              <option value='90d'>Last 90 days</option>
              <option value='1y'>Last year</option>
            </select>
          </div>
        </div>
        <div className='h-80 w-full'>
          {compactTrends.length ? (
            <ResponsiveContainer width='100%' height='100%'>
              <ReBarChart
                data={compactTrends}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray='3 3' vertical={false} />
                <XAxis
                  dataKey='label'
                  tick={{ fontSize: 10 }}
                  interval={0}
                  height={50}
                  angle={-25}
                  textAnchor='end'
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value: number) => value}
                  labelFormatter={(label) => `Date: ${label}`}
                  cursor={{ fill: '#f8fafc' }}
                />
                <Bar dataKey='value' fill='#111827' radius={[4, 4, 0, 0]} maxBarSize={32} />
              </ReBarChart>
            </ResponsiveContainer>
          ) : (
            <div className='h-full flex items-center justify-center text-sm text-gray-600'>
              No trend data available.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
